/**
 * Turn a Firebase auth error into something a person can read.
 *
 * Raw Firebase errors were reaching users as toasts: a wrong password showed
 * "Firebase: Error (auth/invalid-credential)." rather than "Invalid password."
 * AuthService.loginWithIdentifier rethrows the underlying error, and the screens
 * catching it fell back to `err.message` whenever they did not recognise the
 * code — so any unmapped case printed Firebase's internal string.
 *
 * Two things make that fallback unsafe, and both are handled here:
 *
 *   1. The code is not always on `err.code`. Errors that have been wrapped,
 *      re-thrown, or serialised lose it, and only the message still carries
 *      "(auth/…)" inside it. So the code is read from the message too.
 *   2. A message that is not recognised must never be shown. Firebase's strings
 *      are diagnostics, not copy. Anything unmapped falls back to the caller's
 *      own wording instead.
 */

/** Pull `auth/...` out of `err.code`, or out of the message when it is absent. */
export function authErrorCode(err: unknown): string {
  const anyErr = err as { code?: unknown; message?: unknown } | null | undefined;

  const code = typeof anyErr?.code === 'string' ? anyErr.code : '';
  if (code.startsWith('auth/')) return code;

  const message = typeof anyErr?.message === 'string' ? anyErr.message : '';
  const match = message.match(/\(?(auth\/[a-z-]+)\)?/i);
  return match ? match[1].toLowerCase() : '';
}

const MESSAGES: Record<string, string> = {
  // invalid-credential is what modern Firebase returns for a bad password AND
  // for an unknown account, since it stopped distinguishing them to avoid
  // leaking which usernames exist.
  'auth/wrong-password': 'Invalid password.',
  'auth/invalid-credential': 'Invalid password.',
  'auth/invalid-login-credentials': 'Invalid password.',
  'auth/user-not-found': 'No account found with this username.',
  'auth/invalid-email': 'That username or email is not valid.',
  'auth/user-disabled': 'This account has been disabled.',
  'auth/too-many-requests': 'Too many failed attempts. Please try again in a few minutes.',
  'auth/network-request-failed': 'No connection. Check your internet and try again.',
  'auth/requires-recent-login': 'Please sign in again to make this change.',
  'auth/email-already-in-use': 'That email is already in use.',
  'auth/weak-password': 'That password is too short.',
  'auth/popup-closed-by-user': 'Sign-in was cancelled.',
  'auth/cancelled-popup-request': 'Sign-in was cancelled.',
};

/**
 * @param err      whatever was caught
 * @param fallback wording to use when the error is not a recognised auth error.
 *                 Never Firebase's own message.
 */
export function friendlyAuthError(err: unknown, fallback = 'Something went wrong. Please try again.'): string {
  const code = authErrorCode(err);
  if (code && MESSAGES[code]) return MESSAGES[code];

  // A deliberate, human message thrown by our own code is worth showing: it was
  // written to be read. Firebase's is not, and always carries the "auth/" tag
  // or the "Firebase:" prefix, so both are excluded.
  const message = (err as { message?: unknown } | null)?.message;
  if (typeof message === 'string'
      && message.trim()
      && !/firebase/i.test(message)
      && !/auth\//i.test(message)) {
    return message;
  }

  return fallback;
}
